import { ReactComponent as IconDriver } from '@/assets/svgs/Value-driver.svg';
import { ReactComponent as IconData } from "@/assets/svgs/icon-data.svg";
import Button from "@/components/Button";
import Portal from "@/components/Portal";
import Tabs, { Tab } from "@/components/Tabs";
import Toast from '@/components/Toast';
import ApplicablePersonas from "@/components/UseCaseDetails/applicable-personas";
import CurrentParticipants from "@/components/UseCaseDetails/current-participants";
import SearchPersonaParticipantList from "@/components/UseCaseDetails/search-persona-participant-list";
import useDataElements from "@/hooks/use-data-elements";
import usePage from "@/hooks/use-page";
import useProfile from '@/hooks/use-participant-profile';
import usePersona from "@/hooks/use-personas";
import useToast from '@/hooks/use-toast';
import useUseCases from "@/hooks/use-use-cases";
import { useNavigate, useParams, useLocation } from "@/lib/router-dom/index";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index';
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";


const Page = () => {
	/*=================================================
		General
	/================================================*/

	const pageTitle = "Use Case";
	const subPageTitle = "View Info";
	const { goTo } = useNavigate();
	const { useCaseId, organizationId } = useParams();
	const { currentState } = useLocation()
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2);

	const participantData = currentState?.state
	const userType = useSelector(state => state.auth.userType)

	const userTypes = {
		participant: "participant",
		admin: "admin",
		nonParticipant: "nonParticipant"
	}

	const enrollmentStatus = {
		approved: "APPROVED",
		rejected: "REJECTED",
		revoked: "REVOKED",
		pending: "PENDING",
		notEnrolled: "NOT_ENROLLED",
		signdsa: "DSA_REQUESTED",
		informationRequired: "INFO_REQUESTED"
	}

	const toastType = {
		success: "success",
		error: "error"
	}

	const [tabsItems, setTabsItems] = useState([
		{ label: "About Use Case", active: true, id: "use-cases-about_use_case" },
		{ label: "Data Elements", active: false, id: "use-cases-data_elements" },
		{ label: "Applicable Personas", active: false, id: "use-cases-applicable_personas" },
		{ label: "Current Participants", active: false, id: "use-cases-participants" },
	]);

	const [searchBoxValue, setSearchBoxValue] = useState("");
	const [selectedDataElement, setSelectedDataElement] = useState({});

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: []
	});

	const [selectedParticipant, setSelectedParticipant] = useState({});

	const statuses = {
		DEFAULT: 'not-enrolled',
		ENROLLED: 'enrolled',
		PENDING: 'pending',
	}

	const handleCancel = () => setSearchBoxValue("");
	/*=================================================
		Routes
	/================================================*/

	function goToEnrollPersona() {
		goTo(`/use-cases/${useCaseId}/personas`);
	}

	function goToEnrollUseCase() {
		sessionStorage.removeItem('personaItems')
		sessionStorage.setItem("useCaseDetails", JSON.stringify(useCaseItem?.id))
		goTo(`/use-cases/enrol/sgtradex`);
	}

	function goToRequestDemo() {
		return window.open("https://calendly.com/sgtradex-demo/intro-call", '_blank', 'noopener')
	}

	/*=================================================
		Tabs
	/================================================*/

	function selectTab(index) {
		const tabs = [...tabsItems].map((item) => ({ ...item, active: false }));

		tabs[index].active = true;
		setSearchBoxValue("");
		setTabsItems(tabs);
	}

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { currentItem: profile, fetch: fetchProfile } = useProfile()
	useEffect(() => {
		if (userType === userTypes.nonParticipant) {
			fetchProfile()
		}
	}, [])

	const {
		find: findPersonas,
		items: personaItems,
		setItems: setPersonaItems,
		findAndSetCurrentPersona,
		currentItem: currentPersonaItem,
		error: errorPersona
	} = usePersona();

	const {
		find: fetchUseCase,
		view: setUseCaseItem,
		currentItem: useCaseItem,
		items: useCaseItems,
		error: errorUseCase
	} = useUseCases();
	const getUseCase = {
		useCaseId: useCaseId,
		all: true
	};

	if (userType === userTypes.admin && organizationId !== undefined && organizationId !== null) {
		getUseCase['organizationId'] = organizationId;
	}

	useEffect(() => {
		async function getData() {
			await fetchDataElements(getUseCase);
			const personasTemp = await findPersonas(getUseCase);
			const uniquePersonaTemp = uniqBy(personasTemp, "id");
			setPersonaItems(uniquePersonaTemp);
			await fetchUseCase(getUseCase);
		}
		getData();
	}, []);

	useEffect(async () => {
		setUseCaseItem(useCaseItems[0]?.id);
		setSelectedDataElement(useCaseItems[0]?.dataElements[0]);
		setSelectedParticipant(useCaseItems?.[0]?.enrolledParticipants?.[0]);
		if (personaItems[0]?.id) {
			await findAndSetCurrentPersona({ personaId: personaItems[0]?.id, useCaseId: useCaseId })
		}

		const adminBreadcrumbsItems = [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Participant',
				link: `/admin/participants`
			},
			{
				label: participantData?.name,
				link: `/admin/participants/dashboard`,
				state: {data: participantData }
			},
			{
				label: useCaseItems[0]?.name,
				link: ``
			},
		]

		const participantBreadcrumbsItem = [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Use Cases',
				link: `/use-cases`
			},
			{
				label: useCaseItems[0]?.name,
				link: ``
			},
		]

		setBreadcrumbsItems(userType === userTypes.admin ? adminBreadcrumbsItems : participantBreadcrumbsItem)

	}, [useCaseItems]);

	/*=================================================
		Data Elements List
	/================================================*/
	const {
		fetch: fetchDataElements,
		items: dataElements,
		currentItem,
		setCurrentItem,
		error: errorDataElement
	} = useDataElements();

	useEffect(() => {
		if (selectedDataElement?.id) {
			const dataElement = dataElements.find(
				(item) => item.id === selectedDataElement.id
			);
			setCurrentItem(dataElement);
		}
	}, [selectedDataElement]);

	const handleSearch = (value) => {
		setSearchBoxValue(value);
	};

	const handleSelect = (value) => {
		setSelectedDataElement(value);
	};

	const handlePersonaSelect = async (item) => {
		await findAndSetCurrentPersona({ personaId: item, useCaseId: useCaseId });
	};

	const handleParticipantSelect = (item) => {
		setSelectedParticipant(item);
	};

	const DataElementsList = ({ data }) => {
		const list = data
			?.filter(
				(item) =>
					searchBoxValue.trim().length === 0 ||
					item?.name
						?.toLowerCase()
						.includes(searchBoxValue.toLowerCase())
			)
			?.map((item, index) => {
				return (
					<div
						className={`view-info__selector-box ${item?.id === selectedDataElement?.id ? "active" : ""
							}`}
						key={index}
						onClick={() => {
							handleSelect(item);
						}}
					>
						{item?.name}
					</div>
				);
			});

		return (
			<div className="view-info__flex view-info-flex-wrap">{list}</div>
		);
	};

	const ApplicablePersonasList = ({ data }) => {
		const list = data
			?.filter(
				(item) =>
					searchBoxValue.trim().length === 0 ||
					item?.name
						?.toLowerCase()
						.includes(searchBoxValue.toLowerCase())
			)
			?.map((item, index) => {
				return (
					<div
						className={`view-info__selector-box ${item?.id === currentPersonaItem?.id ? "active" : ""
							}`}
						key={index}
						onClick={() => {
							handlePersonaSelect(item?.id);
						}}
					>
						{item?.name}
					</div>
				);
			});

		return (
			<div className="view-info__flex view-info-flex-wrap">{list}</div>
		);
	};

	const ParticipantsList = ({ data }) => {
		const list = data
			?.filter(
				(item) =>
					searchBoxValue.trim().length === 0 ||
					item?.name
						?.toLowerCase()
						.includes(searchBoxValue.toLowerCase())
			)
			?.map((item) => {
				return (
					<div
						className={`view-info__selector-box ${item?.id === selectedParticipant?.id ? "active" : ""
							}`}
						key={item?.id}
						onClick={() => {
							handleParticipantSelect(item);
						}}
					>
						<div
							className="participant-logo"
							style={{ backgroundImage: `url(${item.image})` }}
						></div>
						<div className='view-info__selector-box--participant'>
							<span>{item?.name}</span>
							{isAgentPrincipalPhase2Enabled && item?.sharePrincipals &&
								<p>Enrolled in Agent-principal</p>
							}
						</div>
					</div>

				);
			});

		return (
			<div className="view-info__flex view-info-flex-wrap">{list}</div>
		);
	};

	useEffect(() => {
		addToastMessage(errorPersona, toastType.error)
	}, [errorPersona])

	useEffect(() => {
		addToastMessage(errorUseCase, toastType.error)

	}, [errorUseCase])

	useEffect(() => {
		addToastMessage(errorDataElement, toastType.error)
	}, [errorDataElement])

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="view-info__container">
				<div className="view-info__card">
					<div className="view-info__card-gfx">
						<img src={useCaseItem?.image} alt="" />
					</div>
					<span>{useCaseItem?.name || "---"}</span>
					<div className="view-info__counter-container">
						<div className="view-info__counter">
							<span className="view-info__count">
								{useCaseItem?.dataElements.length || 0}
							</span>
							<span className="view-info__title">
								Data Elements
							</span>
						</div>
						<div className="view-info__counter">
							<span className="view-info__count">
								{useCaseItem?.participants || 0}
							</span>
							<span className="view-info__title">
								Participants
							</span>
						</div>
					</div>

					{(useCaseItem?.status === statuses.DEFAULT) && userType === userTypes.participant && (
						<div className="view-info__button">
							<Button
								text="Enroll Now"
								round
								submit={() => goToEnrollPersona()}
							/>
						</div>
					)}
					{
						userType === userTypes.nonParticipant && (
							<div className="view-info__button">
								{(profile?.organization?.status === enrollmentStatus.notEnrolled || profile?.organization?.status === enrollmentStatus.rejected) && (

									<Button
										text="Enroll Now"
										round
										addClass="yellow--button"
										submit={() => goToEnrollUseCase()}
									/>
								)}
								<Button
									text="Request a Demo"
									round
									ghost
									addClass="white--button"
									submit={() => goToRequestDemo()}
								/>
							</div>
						)
					}
				</div>

				<div className="view-info__content">
					<div className="view-info">
						<div className="view-info__header">
							<Tabs>
								{tabsItems.map((item, index) => (
									<Tab
										data-testid="tab-item"
										id={item.id}
										key={index}
										label={item.label}
										active={item.active}
										onClick={() => selectTab(index)}
									/>
								))}
							</Tabs>
						</div>

						{tabsItems[0].active && (
							<div data-testid="use-case-view" className="view-info__details">
								<div className="view-info__details__description">
									<p className="view-info__details-description">
										{useCaseItem?.description || "---"}
									</p>
									<span className="view-info__details-title">
										Approach
									</span>
									<p className="view-info__details-description">
										{useCaseItem?.approach || "---"}
									</p>
								</div>
								<div className="view-info__details__board">
									<div className="view-info__details__board__driver-header">
										<div><IconDriver /></div>
										<div className="view-info__details__board-title">
											<span>Key Value Drivers</span>
										</div>
									</div>
									{useCaseItem?.keyValueDrivers?.length >
										0 ? (
										<ul>
											{useCaseItem?.keyValueDrivers?.map(
												(item, index) => (
													<li className="view-info__details__board__keyhighlight-text" key={index}>
														{item}
													</li>
												)
											)}
										</ul>
									) : (
										<p className="view-info__details__board__keyhighlight-text">
											---
										</p>
									)}
								</div>
							</div>
						)}

						{tabsItems[1].active && (
							<div data-testid="data-element-view" className="view-use-case-details-wrapper">
								<div className="-left-section">
									<SearchPersonaParticipantList
										searchBoxValue={searchBoxValue}
										DataElementsList={DataElementsList}
										list={useCaseItem?.dataElements}
										handleSearch={handleSearch}
										handleCancel={handleCancel}
										placeholderText={
											"Search by data elements"
										}
										showTotal
									/>
								</div>
								<div className="section-divider-vertical"></div>
								<div className="-right-section">
									<div className="view-info__more-info__header">
										<div className="view-info__title__avatar">
											<IconData />
										</div>
										<div className="view-info__more-info__title-container">
											<span className="view-info__more-info__title">
												{currentItem?.schema?.title || "---"}
											</span>
											<p className="view-info__more-info__description">
												{currentItem?.schema?.description || "---"}
											</p>
										</div>
									</div>
									<div className="view-info__more-info__fields--two-col">
										<div className="view-info__more-info__fields">
											<span className="view-info__more-info__field-title">
												Required Data Fields
											</span>
											<ul className="view-info__more-info__field-list">
												{currentItem?.requiredFields
													?.length > 0
													? currentItem?.requiredFields?.map(
														(item, index) => (
															<li className="view-info__more-info__field-list-item" key={index}>
																{item.name ||
																	"---"}
															</li>
														)
													)
													: "---"}
											</ul>
										</div>
										<div className="view-info__more-info__fields">
											<span className="view-info__more-info__field-title">
												Optional Data Fields
											</span>
											<ul className="view-info__more-info__field-list">
												{currentItem?.optionalFields
													?.length > 0
													? currentItem?.optionalFields?.map(
														(item, index) => (
															<li className="view-info__more-info__field-list-item" key={index}>
																{item.name ||
																	"---"}
															</li>
														)
													)
													: "---"}
											</ul>
										</div>
									</div>
									<div className="view-info__more-info__fields-bottom">
										<span className="view-info__more-info__field-title">
											Attachments
										</span>
										<span className="view-info__more-info__field-list-item">
											{currentItem?.attachmentFields ||
												"---"}
										</span>
									</div>
								</div>
							</div>
						)}
						{tabsItems[2].active && (
							<div data-testid="persona-view" className="view-use-case-details-wrapper">
								<div className="-left-section">
									<SearchPersonaParticipantList
										searchBoxValue={searchBoxValue}
										DataElementsList={
											ApplicablePersonasList
										}
										list={personaItems}
										handleSearch={handleSearch}
										handleCancel={handleCancel}
										placeholderText={"Search by persona"}
									/>
								</div>
								<div className="section-divider-vertical"></div>
								<div className="-right-section">
									<ApplicablePersonas
										selectedPersona={currentPersonaItem}
									/>
								</div>
							</div>
						)}
						{tabsItems[3].active && (
							<div data-testid="participant-view" className="view-use-case-details-wrapper">
								<div className="-left-section">
									<SearchPersonaParticipantList
										searchBoxValue={searchBoxValue}
										DataElementsList={ParticipantsList}
										list={useCaseItem?.enrolledParticipants}
										handleSearch={handleSearch}
										handleCancel={handleCancel}
										placeholderText={
											"Search by participant"
										}
									/>
								</div>
								<div className="section-divider-vertical"></div>
								<div className="-right-section">
									<CurrentParticipants
										selectedParticipant={
											selectedParticipant
										}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			<Toast toastList={toastList} />
		</>
	);
};

export default Page;
